import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { ButtonType } from '@app/components/button';
import { CardComponent } from '@app/components/card';
import { ConfirmDialogComponent } from '@app/components/confirm-dialog';
import { HeadingComponent } from '@app/components/heading';
import { InputComponent, InputIcon } from '@app/components/input';
import { SpinnerComponent } from '@app/components/spinner';
import { ColumnType, TableComponent, TableConfig } from '@app/components/table';
import {
  TextAlign,
  TextColor,
  TextComponent,
  TextHeight,
  TextSize,
  TextType,
  TextWeight,
} from '@app/components/text';
import {
  TextInputComponent,
  TextInputSize,
  TextInputType,
} from '@app/components/text-input';
import { ToastService, ToastType } from '@app/components/toast';
import { ViewComponent, ViewDirection } from '@app/components/view';
import { AdminTenantService } from '@app/services/admin-tenant/admin-tenant.service';
import {
  AdminUserService,
  UserPermission,
} from '@app/services/admin-user/admin-user.service';
import { AdminUsersService } from '@app/services/admin-users/admin-users.service';
import { TranslatorService } from '@app/services/translator/translator.service';
import { Spacing } from '@app/shared/types';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { User, UsersAaptivV2 } from '@skyfit/core-data-library';

@Component({
  standalone: true,
  selector: 'app-manage',
  imports: [
    CommonModule,
    ConfirmDialogComponent,
    TextComponent,
    CardComponent,
    TranslateDirective,
    TranslatePipe,
    SpinnerComponent,
    TableComponent,
    HeadingComponent,
    InputComponent,
    ViewComponent,
    TextInputComponent,
  ],
  templateUrl: './manage.component.html',
  styleUrl: './manage.component.css',
})
export class ManageComponent implements OnInit {
  private readonly translatorService = inject(TranslatorService);
  private readonly adminTenantService = inject(AdminTenantService);
  private readonly adminUsersService = inject(AdminUsersService);
  private readonly adminUserService = inject(AdminUserService);
  private readonly toastService = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly route = inject(ActivatedRoute);
  isLoading = signal(true);
  searchInput = signal('');
  adminUsers = signal([]);
  filteredUsers = computed(() => {
    const filteredUsers = this.adminUsers().filter((u) => {
      const searchInput = this.searchInput();
      return (
        !searchInput ||
        this._isMatch(searchInput, u.name) ||
        this._isMatch(searchInput, u.email)
      );
    });
    return filteredUsers;
  });
  openDialog = signal(false);
  readonly tableConfig: TableConfig = {
    type: 'admins.admins',
    columns: [
      { key: 'name', label: 'Name' },
      { key: 'email', label: 'Email' },
      { key: 'options', label: '', columnType: ColumnType.options },
    ],
  };
  currentUser?: User;
  selectedItem = {
    id: '',
    name: '',
    email: '',
    options: [],
  };
  ButtonType = ButtonType;
  TextType = TextType;
  TextHeight = TextHeight;
  TextWeight = TextWeight;
  TextSize = TextSize;
  TextColor = TextColor;
  TextAlign = TextAlign;
  ViewDirection = ViewDirection;
  Spacing = Spacing;
  InputIcon = InputIcon;
  InputType = TextInputType;
  InputSize = TextInputSize;

  ngOnInit(): void {
    const tenantId = this.adminTenantService.getTenantId();
    this.route.parent.parent.data
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data) => {
        const { user } = data.initialData;
        this.currentUser = user;
      });

    if (tenantId) {
      this.adminUsersService
        .getAdminUsers(tenantId)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (response: UsersAaptivV2) => {
            this.adminUsers.set(this.buildCollection(response.users));
            this.isLoading.set(false);
          },
          error: () => {
            const message = this.translatorService.translate(
              'account.getAdminsError'
            );
            this.toastService.add(message, ToastType.ERROR);
            this.isLoading.set(false);
          },
        });
    } else {
      const message = this.translatorService.translate(
        'account.getAdminsError'
      );
      this.toastService.add(message, ToastType.ERROR);
      this.isLoading.set(false);
    }
  }

  buildCollection(admins) {
    const readUserOption = this.adminUserService.hasPermission(
      UserPermission.READ_USER_OPTIONS
    );

    return admins.map((admin) => ({
      id: admin.userId ?? admin.id,
      name: admin.name ?? admin.title ?? 'N/A',
      email: admin.email ?? admin.subTitle,
      options:
        admin.email !== this.currentUser?.email && readUserOption
          ? [{ key: 'delete', label: 'account.removeAdminRoleButtonLabel' }]
          : null,
    }));
  }

  onAction(item) {
    this.selectedItem = item;
    this.openDialog.set(true);
  }

  onCloseDialog() {
    this.openDialog.set(false);
  }

  onRemove() {
    this.openDialog.set(false);
    this.adminUserService
      .removeAdminUserRole(this.selectedItem.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.adminUsers.update((users) =>
            this.buildCollection(
              users.filter((user) => user.id !== this.selectedItem.id)
            )
          );
          const message = this.translatorService.translate(
            'account.removeAdminRoleFromUserSuccess',
            { name: this.selectedItem.name }
          );
          this.toastService.add(message, ToastType.INFO);
          this.selectedItem = null;
        },
        error: () => {
          const message = this.translatorService.translate(
            'account.removeAdminRoleFromUserError',
            { name: this.selectedItem.name }
          );
          this.toastService.add(message, ToastType.ERROR);
        },
      });
  }

  private _isMatch(input?: string, value?: string): boolean {
    return (
      input && value?.toLocaleLowerCase()?.includes(input?.toLocaleLowerCase())
    );
  }
}
